import { supabase } from './supabase';

const pad2 = (n) => String(n).padStart(2, '0');

export const toISODate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const isValidUrl = (value) => {
  if (!value) return true;
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

export const getSessionUser = () => {
  try {
    const raw = sessionStorage.getItem('currentUser');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

export async function fetchGradesForSchool(schoolId) {
  if (!schoolId) return [];
  const { data, error } = await supabase
    .from('grades')
    .select('id, name, order_index')
    .eq('school_id', schoolId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function fetchGradeSectionsForSchool(schoolId) {
  if (!schoolId) return [];

  // First fetch grade IDs for this school
  const { data: grades, error: gradesError } = await supabase
    .from('grades')
    .select('id')
    .eq('school_id', schoolId);

  if (gradesError) throw gradesError;
  if (!grades || grades.length === 0) return [];

  const gradeIds = grades.map(g => g.id);

  const { data, error } = await supabase
    .from('grade_sections')
    .select('id, grade_id, name, order_index, is_active')
    .in('grade_id', gradeIds)
    .eq('is_active', true)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function fetchAssignmentsInRange({
  schoolId,
  startDateISO,
  endDateISO,
  gradeIds,
  sectionId, // Student's specific section (optional)
}) {
  if (!schoolId || !startDateISO || !endDateISO) return [];

  let q = supabase
    .from('calendar_assignments')
    .select(`
      id,
      school_id,
      grade_id,
      section_id,
      assigned_date,
      title,
      description,
      link_url,
      attachment_path,
      attachment_name,
      attachment_type,
      created_by,
      created_at,
      updated_at,
      created_by_user:users!calendar_assignments_created_by_fkey(
        id,
        full_name,
        first_name,
        last_name
      )
    `)
    .eq('school_id', schoolId)
    .gte('assigned_date', startDateISO)
    .lte('assigned_date', endDateISO)
    .order('assigned_date', { ascending: true })
    .order('created_at', { ascending: true });

  if (Array.isArray(gradeIds) && gradeIds.length > 0) {
    q = q.in('grade_id', gradeIds);
  }

  const { data, error } = await q;
  if (error) throw error;

  // If sectionId is provided (student view), filter to show:
  // 1. Assignments with NO section (whole-grade assignments)
  // 2. Assignments matching the student's specific section
  if (sectionId) {
    return (data || []).filter(a => !a.section_id || a.section_id === sectionId);
  }

  return data || [];
}

export async function uploadAssignmentAttachment({
  file,
  schoolId,
  bucket = 'assignments',
}) {
  if (!file) return null;
  if (!schoolId) {
    throw new Error('Missing schoolId for upload.');
  }

  const ext = file.name?.split('.').pop() || 'bin';
  const timestamp = Date.now();
  const fileName = `${timestamp}.${ext}`;
  const path = `${schoolId}/files/${fileName}`;

  const { error: upErr } = await supabase.storage.from(bucket).upload(path, file);
  if (upErr) throw upErr;

  const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
  return {
    bucket,
    path,
    publicUrl: urlData?.publicUrl || null,
    name: file.name || null,
    type: file.type || null,
    size: file.size || null,
  };
}

export async function createAssignments({
  schoolId,
  teacherId,
  gradeAssignments, // Array of { gradeId, sectionId } objects
  assignedDateISO,
  title,
  description,
  linkUrl,
  attachment,
}) {
  if (!schoolId) throw new Error('Missing schoolId');
  if (!teacherId) throw new Error('Missing teacherId');
  if (!assignedDateISO) throw new Error('Missing assignedDate');
  if (!title) throw new Error('Title is required');
  if (!Array.isArray(gradeAssignments) || gradeAssignments.length === 0) throw new Error('Please select at least one grade/section');

  const rows = gradeAssignments.map((item) => ({
    school_id: schoolId,
    grade_id: item.gradeId,
    section_id: item.sectionId || null,
    assigned_date: assignedDateISO,
    title,
    description: description || null,
    link_url: linkUrl || null,
    attachment_path: attachment?.path || null,
    attachment_name: attachment?.name || null,
    attachment_type: attachment?.type || null,
    created_by: teacherId,
  }));

  const { data, error } = await supabase
    .from('calendar_assignments')
    .insert(rows)
    .select(`
      id,
      school_id,
      grade_id,
      section_id,
      assigned_date,
      title,
      description,
      link_url,
      attachment_path,
      attachment_name,
      attachment_type,
      created_by,
      created_at,
      updated_at,
      created_by_user:users!calendar_assignments_created_by_fkey(
        id,
        full_name,
        first_name,
        last_name
      )
    `);

  if (error) throw error;
  return data || [];
}

export const getAttachmentPublicUrl = (path, bucket = 'assignments') => {
  if (!path) return null;
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data?.publicUrl || null;
};

export async function updateAssignment({ assignmentId, updates }) {
  if (!assignmentId) throw new Error('Missing assignmentId');
  if (!updates || typeof updates !== 'object') throw new Error('Missing updates');

  const { data, error } = await supabase
    .from('calendar_assignments')
    .update(updates)
    .eq('id', assignmentId)
    .select(`
      id,
      school_id,
      grade_id,
      assigned_date,
      title,
      description,
      link_url,
      attachment_path,
      attachment_name,
      attachment_type,
      created_by,
      created_at,
      updated_at,
      created_by_user:users!calendar_assignments_created_by_fkey(
        id,
        full_name,
        first_name,
        last_name
      )
    `)
    .single();

  if (error) throw error;
  return data;
}

export async function deleteAssignment({ assignmentId }) {
  if (!assignmentId) throw new Error('Missing assignmentId');
  const { error } = await supabase.from('calendar_assignments').delete().eq('id', assignmentId);
  if (error) throw error;
  return true;
}


